import { t } from 'i18next';
import { useHandlers } from '../../../context/page-context';
import { cn } from '../../../lib/styles';
import { ExternalLink } from '../external-link/external-link.spark';
import { Button, Heading, Icon, LearnMoreLink, Menu, Text } from '../index.spark';
import type { PageHeaderProps, PageProps } from './types';

export function Page(props: PageProps) {
  return (
    <div
      className={cn(
        'mx-auto flex flex-col gap-4',
        props.fillHeight && 'min-h-0 flex-1',
        props.hasSideBar
          ? props.narrow
            ? 'w-full max-w-2xl xl:w-fit xl:max-w-none'
            : 'w-fit'
          : props.narrow
            ? 'w-full max-w-2xl'
            : 'w-full max-w-7xl',
      )}
    >
      {props.title && <PageHeader {...props} />}
      <div
        className={cn(
          props.fillHeight && 'min-h-0 flex-1',
          props.hasSideBar ? 'gap-5 xl:grid xl:grid-cols-[1fr_15rem]' : 'flex flex-col gap-5',
        )}
      >
        {props.children}
      </div>
      {props.learnMoreUrl && props.footerDescription && (
        <div className="mt-4 w-full text-center">
          <ExternalLink link={props.learnMoreUrl}>{props.footerDescription}</ExternalLink>
        </div>
      )}
    </div>
  );
}

function PageHeader({
  title,
  description,
  learnMoreUrl,
  backUrl,
  backUrlOpts,
  secondaryActions,
  primaryAction,
}: PageHeaderProps) {
  const { navigationHandler } = useHandlers();
  const singleSecondaryAction = secondaryActions?.length === 1 ? secondaryActions[0] : undefined;
  return (
    <header className="flex shrink-0 flex-col gap-2">
      <span className="flex items-center justify-between">
        <span className="flex flex-nowrap items-center gap-1">
          {backUrl && (
            <button
              type="button"
              // biome-ignore lint/style/noNonNullAssertion: backUrl should always be defined here, but type checker doesn't know that
              onClick={() => navigationHandler.navigate(backUrl!, backUrlOpts)}
              className="h-min rounded-md p-1 transition hover:bg-gray-200"
            >
              <Icon name="back" size="lg" />
            </button>
          )}
          <Heading level="h1" style="xl">
            {title}
          </Heading>
        </span>
        <span className="inline-flex gap-2">
          {singleSecondaryAction && (
            <span className={cn({ 'hidden sm:contents': singleSecondaryAction.hideOnMobile })}>
              <Button
                key={singleSecondaryAction.label}
                disabled={singleSecondaryAction.disabled}
                loading={singleSecondaryAction.loading}
                icon={singleSecondaryAction.icon}
                variant={singleSecondaryAction.danger ? 'danger' : 'secondary'}
                onClick={singleSecondaryAction.onClick}
              >
                {singleSecondaryAction.label}
              </Button>
            </span>
          )}

          {secondaryActions && secondaryActions.length > 1 && (
            <Menu
              collapsible
              items={secondaryActions.map((action) => ({
                label: action.label,
                icon: action.icon,
                onClick: action.onClick,
                disabled: action.disabled,
                danger: action.danger,
              }))}
              buttonText={t('buttons:more_actions')}
              align="end"
            />
          )}

          {primaryAction && (
            <Button
              key={primaryAction.label}
              icon={primaryAction.icon}
              variant="primary"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              loading={primaryAction.loading}
              collapsible
            >
              {primaryAction.label}
            </Button>
          )}
        </span>
      </span>
      <Text variant="secondary">
        {description}
        {learnMoreUrl && (
          <>
            {' '}
            <LearnMoreLink link={learnMoreUrl} />
          </>
        )}
      </Text>
    </header>
  );
}
